// services/salesService.js

const API_BASE_URL = 'http://staging.maamaas.com:8080/protohub/api';

export const salesService = {
  // Fetch all enquiries
  async getAllEnquiries() {
    try {
      const response = await fetch(`${API_BASE_URL}/user/enquiry/getAll`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add any auth headers if needed
          // 'Authorization': `Bearer ${yourToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      throw error;
    }
  },

  // Get enquiry statistics
  getEnquiryStats(enquiries) {
    if (!enquiries || enquiries.length === 0) {
      return {
        total: 0,
        byStatus: {},
        totalValue: 0,
        pendingValue: 0,
      };
    }

    const stats = {
      total: enquiries.length,
      byStatus: {},
      totalValue: 0,
      pendingValue: 0,
    };

    enquiries.forEach(enquiry => {
      const status = enquiry.enquiryStatus || 'PENDING';
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

      // Parse budget value for calculations
      const budgetValue = this.parseBudgetValue(enquiry.expectedBudgetRange);
      if (status === 'PENDING' || status === 'DRAFT') {
        stats.pendingValue += budgetValue;
      }
      stats.totalValue += budgetValue;
    });

    return stats;
  },

  // Parse budget range to approximate value
  parseBudgetValue(budgetRange) {
    if (!budgetRange) return 0;
    
    const budgetMap = {
      'BELOW_50000': 25000,
      'BETWEEN_50000_AND_100000': 75000,
      'BETWEEN_100000_AND_500000': 300000,
      'BETWEEN_500000_AND_1000000': 750000,
      'ABOVE_1000000': 1500000,
    };

    return budgetMap[budgetRange] || 0;
  },

  // Get pipeline stage distribution
  getPipelineStages(enquiries) {
    const stages = ['Sourcing', 'Enquiry', 'Quote', 'Approval', 'PO', 'Execution', 'Payment'];
    const stageCounts = {};
    const stageValues = {};

    stages.forEach(stage => {
      stageCounts[stage] = 0;
      stageValues[stage] = 0;
    });

    enquiries.forEach(enquiry => {
      const status = enquiry.enquiryStatus || 'PENDING';
      let stage = 'Enquiry';
      
      // Map status to pipeline stage
      if (status === 'DRAFT' || status === 'PENDING') stage = 'Sourcing';
      else if (status === 'QUOTE_SUBMITTED') stage = 'Quote';
      else if (status === 'APPROVED') stage = 'Approval';
      else if (status === 'IN_PROGRESS') stage = 'Execution';
      else if (status === 'COMPLETED') stage = 'Payment';
      
      stageCounts[stage] = (stageCounts[stage] || 0) + 1;
      stageValues[stage] = (stageValues[stage] || 0) + this.parseBudgetValue(enquiry.expectedBudgetRange);
    });

    return { stageCounts, stageValues };
  },

  // Get enquiry status distribution
  getEnquiryStatusDistribution(enquiries) {
    const statusMap = {
      'DRAFT': 'Pending Quote',
      'PENDING': 'Pending Quote',
      'QUOTE_SUBMITTED': 'Quote Submitted',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected',
      'REVISION': 'Revision',
    };

    const distribution = {};
    enquiries.forEach(enquiry => {
      const status = enquiry.enquiryStatus || 'DRAFT';
      const displayStatus = statusMap[status] || 'Pending Quote';
      distribution[displayStatus] = (distribution[displayStatus] || 0) + 1;
    });

    return distribution;
  },

  // Get project status distribution
  getProjectStatusDistribution(enquiries) {
    const distribution = {
      'Initiated': 0,
      'In Progress': 0,
      'Completed': 0,
    };

    enquiries.forEach(enquiry => {
      const stage = enquiry.developmentStage || 'DESIGN';
      if (stage === 'DESIGN' || stage === 'PROTOTYPE') {
        distribution['Initiated']++;
      } else if (stage === 'WORKING_PROTOTYPE' || stage === 'PRODUCTION') {
        distribution['In Progress']++;
      } else if (stage === 'FINAL') {
        distribution['Completed']++;
      }
    });

    return distribution;
  },

  // Get recent enquiries (last 3) - FIXED to show projectName
  getRecentEnquiries(enquiries) {
    if (!enquiries || enquiries.length === 0) return [];
    
    // Sort by id descending (assuming higher id = newer)
    const sorted = [...enquiries].sort((a, b) => b.id - a.id);
    
    return sorted.slice(0, 3).map(enquiry => ({
      id: `ENQ-${enquiry.id}${this.generateRandomSuffix()}`,
      projectName: enquiry.projectName || 'Unnamed Project', // Use actual projectName
      quote: this.formatBudgetValue(this.parseBudgetValue(enquiry.expectedBudgetRange)),
      status: this.mapEnquiryStatus(enquiry.enquiryStatus),
      clientStatus: this.mapClientStatus(enquiry.enquiryStatus),
      owner: enquiry.userId ? `User ${enquiry.userId}` : 'Guest', // Use userId as owner
      raw: enquiry,
    }));
  },

  // Helper methods
  generateRandomSuffix() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },

  formatBudgetValue(value) {
    if (value === 0) return '—';
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return `₹${value.toLocaleString()}`;
  },

  mapEnquiryStatus(status) {
    const statusMap = {
      'DRAFT': 'Pending Quote',
      'PENDING': 'Pending Quote',
      'QUOTE_SUBMITTED': 'Quote Submitted',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected',
      'REVISION': 'Revision',
    };
    return statusMap[status] || 'Pending Quote';
  },

  mapClientStatus(status) {
    const statusMap = {
      'DRAFT': '—',
      'PENDING': '—',
      'QUOTE_SUBMITTED': 'Negotiation',
      'APPROVED': 'Accepted',
      'REJECTED': 'Rejected',
      'REVISION': 'Revision',
    };
    return statusMap[status] || '—';
  },
};